// Карточка задачи лонча: название, дорожка и трек, даты, статус, заметка. Сохранение с expect_updated_at
// живой строки: чужая правка между открытием и сохранением — conflict, не тихая перезапись.
import { useCanWrite } from '@core/auth/access';
import { useState, type FormEvent } from 'react';
import { Modal, TabPanels } from '@core/ui/Modal';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { ObjectHistory } from '../history/ObjectHistory';
import { DeleteConfirm, FormError, ModalFooter, SAVE_CONFLICT, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { data, useTable } from '@core/store';
import { EntitySelect } from '@core/ui/EntitySelect';
import { LAUNCH_STATUSES, STATUS_LOOK, blockerCandidates, openBlockersAmong, planLaunchLinks, planLaunchTaskSave, type LaunchTask, type LaunchTaskDraft, type Swimlane, type Track } from './planner';

const FORM_ID = 'launch-task-form';

export function LaunchTaskModal({ draft, swimlanes, tracks, launchTasks, onClose }: { draft: LaunchTaskDraft; swimlanes: Swimlane[]; tracks: Track[]; launchTasks: LaunchTask[]; onClose: () => void }) {
  const canWrite = useCanWrite();
  const links = useTable('launch_link');
  const [d, setD] = useState(draft);
  const [tab, setTab] = useState<'main' | 'history'>('main');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const byId = new Map(launchTasks.map((t) => [t.id, t]));
  const live = d.id ? byId.get(d.id) : undefined;
  const others = d.id ? blockerCandidates(d.id, d.blockedBy, links.values(), launchTasks) : launchTasks.filter((t) => !d.blockedBy.includes(t.id)).sort((a, b) => a.title.localeCompare(b.title));
  const blocks = d.id ? [...links.values()].filter((l) => l.blocker_id === d.id).map((l) => byId.get(l.blocked_id)).filter((t): t is LaunchTask => !!t) : [];
  const openBlockers = openBlockersAmong(d.blockedBy, byId);

  async function save(e: FormEvent) {
    e.preventDefault();
    const plan = planLaunchTaskSave(d, live, () => crypto.randomUUID());
    if ('error' in plan) { setError(plan.error); return; }
    const changes = [...plan.changes, ...planLaunchLinks(plan.taskId, d.blockedBy, links.values())];
    if (changes.length === 0) { onClose(); return; }
    setBusy(true); setError(null);
    const r = await data.applyChanges(changes, { kind: d.id ? 'edit_launch_task' : 'create_launch_task', launch_task_id: plan.taskId });
    setBusy(false);
    if (!r.ok) { setError(r.conflict ? SAVE_CONFLICT : r.error); return; }
    onClose();
  }
  async function remove() {
    if (!d.id) return;
    setBusy(true);
    const r = await data.applyChanges([{ table: 'launch_task', id: d.id, delete: true }], { kind: 'delete_launch_task', launch_task_id: d.id });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={onClose} readOnly={!canWrite} left={d.id && <DeleteConfirm label="Delete" question={<>Delete «{d.title}»? It stays in the history.</>} busy={busy} onDelete={() => void remove()} />} />
  );
  const form = (
    <form id={FORM_ID} onSubmit={(e) => void save(e)} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="flex flex-col gap-4">
      <label className={label}>Title
        <input className={input} value={d.title} autoFocus required onChange={(e) => setD({ ...d, title: e.target.value })} />
      </label>
      <div className={label}>
        <span>Row</span>
        <EntitySelect label="Track" value={d.track_id} disabled={!canWrite} onChange={(id) => { if (id) setD({ ...d, track_id: id }); }}
          options={swimlanes.flatMap((s) => tracks.filter((t) => t.swimlane_id === s.id).map((t) => ({ id: t.id, name: t.name, color: s.color, group: s.name })))} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className={label}>Start
          <input type="date" className={input} value={d.start_date} required onChange={(e) => setD({ ...d, start_date: e.target.value })} />
        </label>
        <label className={label}>End
          <input type="date" className={input} value={d.end_date} min={d.start_date} required onChange={(e) => setD({ ...d, end_date: e.target.value })} />
        </label>
      </div>
      <div className={label}>Status
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Status">
          {LAUNCH_STATUSES.map((s) => {
            const look = STATUS_LOOK[s];
            const on = d.status === s;
            return (
              <button key={s} type="button" role="radio" aria-checked={on} data-launch-status={s}
                className={`min-h-0 px-3 py-1 rounded-md border text-sm font-medium ${on ? 'ring-2 ring-offset-1 ring-gray-700' : ''}`}
                style={{ background: look.bg, borderColor: look.border, color: look.text }} onClick={() => setD({ ...d, status: s })}>
                {look.glyph ? `${look.glyph} ` : ''}{look.label}
              </button>
            );
          })}
        </div>
        {openBlockers.length > 0 && d.status !== 'done' && <span className="text-sm text-red-700">Shown as blocked until {openBlockers.map((b) => `«${b.title}»`).join(', ')} {openBlockers.length > 1 ? 'are' : 'is'} done.</span>}
      </div>
      {/* связи: кто блокирует эту задачу (правится), кого блокирует она (справочно, правится в их карточках) */}
      <div className={label}>Blocked by
        <div className="flex flex-wrap gap-1.5 items-center" data-blocked-by>
          {d.blockedBy.map((id) => {
            const t = byId.get(id);
            return (
              <span key={id} className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-sm font-medium" data-blocker={id}>
                {t?.title ?? '?'}
                <button type="button" className="min-h-0 text-gray-500 hover:text-red-700 leading-none" aria-label={`Remove blocker ${t?.title ?? ''}`} onClick={() => setD({ ...d, blockedBy: d.blockedBy.filter((x) => x !== id) })}>×</button>
              </span>
            );
          })}
          <EntitySelect action size="sm" className="w-48" label="Add a blocker" placeholder="Add a blocker…" disabled={!canWrite || others.length === 0}
            options={others.map((t) => ({ id: t.id, name: t.title }))} value={null} onChange={(id) => { if (id) setD({ ...d, blockedBy: [...d.blockedBy, id] }); }} />
        </div>
        {blocks.length > 0 && <span className="text-xs text-gray-500 font-normal">Blocks: {blocks.map((t) => t.title).join(', ')}</span>}
      </div>
      <label className={label}>Notes
        <textarea className={`${input} min-h-24`} value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} />
      </label>
      <FormError error={error} />
    </form>
  );
  const head = d.id ? <SegmentedControl role="tabs" label="Task card" className="mb-4" value={tab} onChange={setTab} items={[{ key: 'main', label: 'Task' }, { key: 'history', label: 'History' }]} /> : undefined;
  return (
    <Modal open onClose={onClose} title={d.id ? 'Launch task' : 'New launch task'} head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['main', readOnlyFields(!canWrite, form)], ['history', <ObjectHistory table="launch_task" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}
